import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import VerificationBadges from '../verification/VerificationBadges';

const AdminDashboard = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState({
    users: { total: 0, active: 0, verified: 0, flagged: 0, newThisMonth: 0, newThisWeek: 0 },
    flags: { total: 0, unresolved: 0 },
    trustScoreDistribution: [],
    verificationStats: { emailVerified: 0, phoneVerified: 0, socialVerified: 0, identityVerified: 0 },
    reviewQualityStats: [],
    recentFlags: []
  });

  // Users management
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [usersFilters, setUsersFilters] = useState({
    search: '',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Flagged reviews
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [reviewsPagination, setReviewsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalFlagged: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [reviewsFilters, setReviewsFilters] = useState({
    riskLevel: 'all'
  });

  useEffect(() => {
    if (isOpen && isAuthenticated && user?.role === 'admin') {
      fetchDashboardStats();
      fetchUsers();
      fetchFlaggedReviews();
    }
  }, [isOpen, isAuthenticated, user]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...usersFilters
      });
      
      const response = await fetch(`/api/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data.users);
        setUsersPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlaggedReviews = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...reviewsFilters
      });
      
      const response = await fetch(`/api/admin/reviews/flagged?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlaggedReviews(data.data.flaggedReviews);
        setReviewsPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch flagged reviews:', error);
      setError('Failed to load flagged reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (userId, action, data = {}) => {
    try {
      setLoading(true);
      let endpoint = '';
      let method = 'POST';
      let body = data;

      switch (action) {
        case 'activate':
        case 'deactivate':
          endpoint = `/api/admin/users/${userId}/status`;
          method = 'PUT';
          body = { isActive: action === 'activate', ...data };
          break;
        case 'flag':
          endpoint = `/api/admin/users/${userId}/flag`;
          method = 'POST';
          break;
        case 'resolve_flag':
          endpoint = `/api/admin/users/${userId}/flags/${data.flagId}/resolve`;
          method = 'PUT';
          break;
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        fetchUsers(usersPagination.currentPage);
        fetchDashboardStats();
        const result = await response.json();
        alert(result.message || 'Action completed successfully');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Action failed');
      }
    } catch (error) {
      console.error('User action failed:', error);
      setError(error.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (reviewId, action, notes = '') => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/reviews/${reviewId}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ action, notes })
      });

      if (response.ok) {
        fetchFlaggedReviews(reviewsPagination.currentPage);
        fetchDashboardStats();
        const result = await response.json();
        alert(result.message || 'Review action completed successfully');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Review action failed');
      }
    } catch (error) {
      console.error('Review action failed:', error);
      setError(error.message || 'Review action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return '#4CAF50';
      case 'medium': return '#FF9800';
      case 'high': return '#f44336';
      case 'critical': return '#9C27B0';
      default: return '#666';
    }
  };

  if (!isOpen || !isAuthenticated || user?.role !== 'admin') return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '95%',
          maxWidth: '1200px',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
          
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
            Admin Dashboard
          </h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            User management and content moderation
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9'
        }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'users', label: 'Users' },
            { id: 'reviews', label: 'Flagged Reviews' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 8px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                color: activeTab === tab.id ? '#2196F3' : '#666',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #2196F3' : 'none'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {activeTab === 'overview' && (
            <div>
              {/* Stats Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                    {dashboardStats.users.total}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Total Users</div>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#e8f5e8',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
                    {dashboardStats.users.verified}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Verified Users</div>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#fff3e0',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                    {dashboardStats.users.flagged}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Flagged Users</div>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#ffebee',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>
                    {dashboardStats.flags.unresolved}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Unresolved Flags</div>
                </div>
              </div>

              {/* Recent Flags */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Recent Flags
                </h3>
                {dashboardStats.recentFlags.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    No recent flags
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {dashboardStats.recentFlags.map((flag, index) => (
                      <div key={index} style={{
                        padding: '12px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600' }}>
                            {flag.username} ({flag.email})
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {flag.flagType}: {flag.reason}
                          </div>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {formatDate(flag.flaggedAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Verification Stats */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Verification Statistics
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4CAF50' }}>
                      {dashboardStats.verificationStats.emailVerified}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Email Verified</div>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2196F3' }}>
                      {dashboardStats.verificationStats.phoneVerified}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Phone Verified</div>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#FF9800' }}>
                      {dashboardStats.verificationStats.socialVerified}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Social Linked</div>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#9C27B0' }}>
                      {dashboardStats.verificationStats.identityVerified}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Identity Verified</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div>
              {/* Filters */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={usersFilters.search}
                  onChange={(e) => setUsersFilters(prev => ({ ...prev, search: e.target.value }))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '200px'
                  }}
                />

                <select
                  value={usersFilters.status}
                  onChange={(e) => setUsersFilters(prev => ({ ...prev, status: e.target.value }))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '120px'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="flagged">Flagged</option>
                  <option value="verified">Verified</option>
                </select>

                <button
                  onClick={() => fetchUsers(1)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Search
                </button>
              </div>

              {/* Users List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {users.map((user) => (
                  <div key={user._id} style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div style={{ fontWeight: '600', fontSize: '16px' }}>
                          {user.username}
                        </div>
                        <VerificationBadges user={user} size="small" />
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {user.email} • Trust Score: {user.trustScore || 0}/100
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Created: {formatDate(user.createdAt)} • Last Active: {formatDate(user.lastActiveAt)}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleUserAction(user._id, user.isActive ? 'deactivate' : 'activate')}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: user.isActive ? '#f44336' : '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>

                      <button
                        onClick={() => {
                          const reason = prompt('Enter reason for flagging:');
                          if (reason) {
                            handleUserAction(user._id, 'flag', {
                              type: 'suspicious_activity',
                              reason: reason
                            });
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Flag
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => fetchUsers(usersPagination.currentPage - 1)}
                  disabled={!usersPagination.hasPrevPage}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: usersPagination.hasPrevPage ? '#2196F3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: usersPagination.hasPrevPage ? 'pointer' : 'not-allowed'
                  }}
                >
                  Previous
                </button>

                <span style={{ fontSize: '14px', color: '#666' }}>
                  Page {usersPagination.currentPage} of {usersPagination.totalPages}
                </span>

                <button
                  onClick={() => fetchUsers(usersPagination.currentPage + 1)}
                  disabled={!usersPagination.hasNextPage}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: usersPagination.hasNextPage ? '#2196F3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: usersPagination.hasNextPage ? 'pointer' : 'not-allowed'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              {/* Filters */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                alignItems: 'center'
              }}>
                <select
                  value={reviewsFilters.riskLevel}
                  onChange={(e) => setReviewsFilters(prev => ({ ...prev, riskLevel: e.target.value }))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '120px'
                  }}
                >
                  <option value="all">All Risk Levels</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>

                <button
                  onClick={() => fetchFlaggedReviews(1)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Filter
                </button>
              </div>

              {/* Flagged Reviews */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {flaggedReviews.map((reviewQuality) => (
                  <div key={reviewQuality._id} style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    border: `2px solid ${getRiskColor(reviewQuality.riskLevel)}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <div style={{ fontWeight: '600', fontSize: '16px' }}>
                            Review by {reviewQuality.user?.username || 'Unknown'}
                          </div>
                          <div style={{
                            padding: '2px 8px',
                            backgroundColor: getRiskColor(reviewQuality.riskLevel),
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {reviewQuality.riskLevel.toUpperCase()}
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Quality Score: {reviewQuality.overallScore}/100
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatDate(reviewQuality.analysis.analyzedAt)}
                      </div>
                    </div>

                    <div style={{
                      padding: '12px',
                      backgroundColor: '#fff',
                      borderRadius: '6px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                        <strong>Review Content:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {reviewQuality.review?.content || 'Content not available'}
                      </div>
                    </div>

                    {reviewQuality.flags.length > 0 && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#ffebee',
                        borderRadius: '6px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ fontSize: '14px', marginBottom: '8px', color: '#c62828' }}>
                          <strong>Flags:</strong>
                        </div>
                        {reviewQuality.flags.map((flag, index) => (
                          <div key={index} style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                            • {flag.type.replace(/_/g, ' ')}: {flag.reason}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleReviewAction(reviewQuality._id, 'approve')}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Approve
                      </button>

                      <button
                        onClick={() => {
                          const notes = prompt('Enter reason for rejection:');
                          if (notes) {
                            handleReviewAction(reviewQuality._id, 'reject', notes);
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Reject
                      </button>

                      <button
                        onClick={() => handleReviewAction(reviewQuality._id, 'escalate')}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Escalate
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => fetchFlaggedReviews(reviewsPagination.currentPage - 1)}
                  disabled={!reviewsPagination.hasPrevPage}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: reviewsPagination.hasPrevPage ? '#2196F3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: reviewsPagination.hasPrevPage ? 'pointer' : 'not-allowed'
                  }}
                >
                  Previous
                </button>

                <span style={{ fontSize: '14px', color: '#666' }}>
                  Page {reviewsPagination.currentPage} of {reviewsPagination.totalPages}
                </span>

                <button
                  onClick={() => fetchFlaggedReviews(reviewsPagination.currentPage + 1)}
                  disabled={!reviewsPagination.hasNextPage}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: reviewsPagination.hasNextPage ? '#2196F3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: reviewsPagination.hasNextPage ? 'pointer' : 'not-allowed'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;